import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-location";
import { z } from "zod";

import { useLanguage } from "../../../../../i18n/useLanguage";
import { HashLink } from "../../../../../navigation/HashLink";
import { useAddLineItemToCart } from "../../../../../queries/useAddLineItemToCart";
import { useUrl } from "../../../../../stores/hooks/useUrl";
import {
  AnyOrderItem,
  OptionalLineItem,
  OrderDetails,
} from "../../../../../stores/slices/cartSlice";
import { Button, PrimaryButton } from "../../../../../ui/Button/Button";
import { Dialog } from "../../../../../ui/Dialog/Dialog";
import { DialogHeader } from "../../../../../ui/Dialog/DialogHeader";
import { Input } from "../../../../../ui/Input/Input";
import { Textarea } from "../../../../../ui/Textarea/Textarea";
import { useForm } from "../../../../../util/useForm";
import { useOrderDetails } from "../../../useOrderDetails";
import { priceMatchDialogHash } from "./getPriceMatchDilalogLink";

const getLineItem = (
  lineItemId: string,
  orderDetails?: OrderDetails
): [] | [AnyOrderItem, OptionalLineItem] => {
  if (orderDetails) {
    for (const orderItem of orderDetails.orderItemList) {
      for (const lineItem of orderItem.additionalOptionalLineItemList) {
        if (lineItem.lineItemId === lineItemId) {
          return [orderItem, lineItem];
        }
      }
    }
  }
  return [];
};

const schema = z.object({
  lineItemNotes: z.string().nonempty({ message: "form.error.lineItemNotes" }),
  lineItemPrice: z
    .number({ coerce: true })
    .positive({ message: "form.error.packagePrice" })
    .refine((value) => /^\d+(\.\d{1,2})?$/.test(value.toString()), {
      message: "form.error.pricePrecision",
    }),
  lineItemWorkOrderNumber: z.string(),
});

export const PriceMatchDialog: FunctionComponent = () => {
  const { t } = useTranslation();
  const { hash } = useUrl();
  const lineItemId = hash.replace(priceMatchDialogHash, "");
  const addLineItemToCart = useAddLineItemToCart();
  const { data } = useOrderDetails();
  const [orderItem, lineItem] = getLineItem(lineItemId, data);
  const [language] = useLanguage();

  const [handleForm] = useForm({
    async onSubmit({ lineItemNotes, lineItemPrice, lineItemWorkOrderNumber }) {
      if (orderItem && lineItem) {
        await addLineItemToCart.send(orderItem, lineItem, {
          lineItemNotes,
          lineItemPrice: String(lineItemPrice),
          lineItemWorkOrderNumber,
        });
        navigate("#");
      }
    },
    schema,
  });

  return (
    <Dialog id="price-match-dialog" visible={!!lineItem}>
      <DialogHeader
        onClose={null}
        subtitle={lineItem?.title[language] ?? lineItem?.title.en}
        title={t("cart.addItem")}
      />
      <form className="p-4 grid gap-6" noValidate onSubmit={handleForm}>
        <fieldset className="grid gap-2">
          <label className="text-xs text-ink-2 font-thin" htmlFor="reset-email">
            {t("form.label.workOrderNumber")}
          </label>
          <Input
            aria-label={t("form.label.workOrderNumber")}
            name="lineItemWorkOrderNumber"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <label className="text-xs text-ink-2 font-thin" htmlFor="reset-email">
            {t("form.label.discount")}
          </label>
          <Input
            aria-label={t("form.label.discount")}
            before="$"
            name="lineItemPrice"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <label className="text-xs text-ink-2 font-thin" htmlFor="reset-email">
            {t("form.label.priceMatchNotes")}
          </label>
          <Textarea
            aria-label={t("form.label.priceMatchNotes")}
            name="lineItemNotes"
          />
        </fieldset>
        <fieldset className="grid grid-cols-2 gap-2">
          <HashLink hash="#">
            <Button aria-label={t("form.label.cancel")}>
              {t("form.label.cancel")}
            </Button>
          </HashLink>
          <PrimaryButton
            aria-label={t("form.label.add")}
            busy={addLineItemToCart.loading}
            type="submit"
          >
            {t("form.label.add")}
          </PrimaryButton>
        </fieldset>
      </form>
    </Dialog>
  );
};
